import type { ReactNode } from "react";
import { useEffect } from "react";
import ChevronLeftRounded from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRounded from "@mui/icons-material/ChevronRightRounded";
import { AppBar, Box, Stack, Toolbar } from "@mui/material";
import { Link } from "react-router-dom";

import { NavigationScrollButton } from "../components/navigation-scroll-button";
import { NavigationTab } from "../components/navigation-tab";
import type { NavbarItem } from "../types";
import { useScrollControls } from "../hooks/use-scroll-controls";
import { AppBarIcon } from "../app-bar-icon";
import { AppBarLogo } from "../app-bar-logo";

const SCROLL_AMOUNT = 300;

export interface PrimaryNavbarProps {
	readonly logo: ReactNode;
	readonly icon: ReactNode;
	readonly navbarItems: NavbarItem[];
	readonly actions?: ReactNode;
	readonly logoLink?: string;
}

export const PrimaryNavbar = ({ logo, icon, navbarItems, actions, logoLink = "/" }: PrimaryNavbarProps) => {
	const { ref, scrollHidden, handleScrollClick, isAtScrollStart, isAtScrollEnd, onScroll, handleRefreshScroll } =
		useScrollControls();

	const navbarKey = navbarItems.map((item) => item.link).join(",");

	useEffect(() => {
		handleRefreshScroll();
	}, [navbarKey, handleRefreshScroll]);

	return (
		<AppBar position="static" sx={{ height: 64 }}>
			<Toolbar
				sx={{
					minHeight: "64px !important",
					height: 64,
					display: "grid",
					gridTemplateColumns:
						actions === undefined ? "min-content minmax(0, 1fr)" : "min-content minmax(0, 1fr) max-content",
					gap: 2,
				}}
			>
				<Box component={Link} to={logoLink} sx={{ display: "flex", alignItems: "center" }}>
					<Box sx={{ display: { xs: "none", lg: "flex" } }}>
						<AppBarLogo>{logo}</AppBarLogo>
					</Box>
					<Box sx={{ display: { xs: "flex", lg: "none" } }}>
						<AppBarIcon>{icon}</AppBarIcon>
					</Box>
				</Box>

				<Box
					sx={{
						height: "100%",
						display: "grid",
						gridTemplateColumns: scrollHidden ? "1fr" : "min-content 1fr min-content",
					}}
				>
					{scrollHidden === false && (
						<NavigationScrollButton disabled={isAtScrollStart()} onClick={() => handleScrollClick(-SCROLL_AMOUNT)}>
							<ChevronLeftRounded />
						</NavigationScrollButton>
					)}

					<Box
						ref={ref}
						onScroll={(event) => onScroll(event.currentTarget.scrollLeft)}
						sx={{
							display: "flex",
							overflowX: "auto",
							overflowY: "hidden",
							"::-webkit-scrollbar": {
								display: "none",
							},
						}}
					>
						{navbarItems.map((tab) => (
							<NavigationTab key={tab.link} dark to={tab.link} data-pendoid={tab.pendoId}>
								{tab.text}
							</NavigationTab>
						))}
					</Box>

					{scrollHidden === false && (
						<NavigationScrollButton disabled={isAtScrollEnd()} onClick={() => handleScrollClick(SCROLL_AMOUNT)}>
							<ChevronRightRounded />
						</NavigationScrollButton>
					)}
				</Box>

				{actions !== undefined && (
					<Stack direction="row" alignItems="center" spacing={2} height="100%" sx={{ width: "100%", minWidth: 0 }}>
						{actions}
					</Stack>
				)}
			</Toolbar>
		</AppBar>
	);
};
